/**
 * Timezone-safe date utilities
 *
 * IMPORTANT: This app handles date-only values (YYYY-MM-DD) that should be
 * interpreted consistently regardless of the user's timezone.
 *
 * For example, a reunion on "2025-12-13" should always display as December 13,
 * whether the user is in Mexico City (UTC-6) or Cancún (UTC-5).
 */

/**
 * Parse a date string (YYYY-MM-DD) and return day, month, year
 * WITHOUT any timezone conversion
 */
export function parseDateString(dateString: string): { day: number; month: number; year: number } {
  const [year, month, day] = dateString.split('-').map(Number);
  return { day, month, year };
}

/**
 * Format a date string (YYYY-MM-DD) for display in Spanish
 * WITHOUT any timezone conversion
 */
export function formatDateES(dateString: string, options: {
  format?: 'short' | 'long' | 'numeric';
  includeWeekday?: boolean;
} = {}): string {
  const { day, month, year } = parseDateString(dateString);
  const { format = 'long', includeWeekday = false } = options;

  const monthNames = {
    short: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
    long: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
  };

  if (format === 'numeric') {
    return `${day}/${month}/${year}`;
  }

  const monthName = format === 'short' ? monthNames.short[month - 1] : monthNames.long[month - 1];
  let result = `${day} ${monthName} ${year}`;

  if (includeWeekday) {
    const weekdayName = getWeekdayFromDateString(dateString);
    result = `${weekdayName}, ${result}`;
  }

  return result;
}

/**
 * Get day of month from date string (YYYY-MM-DD)
 * WITHOUT any timezone conversion
 */
export function getDayFromDateString(dateString: string): number {
  return parseDateString(dateString).day;
}

/**
 * Get month name from date string (YYYY-MM-DD)
 * WITHOUT any timezone conversion
 */
export function getMonthNameFromDateString(dateString: string, short: boolean = true): string {
  const { month } = parseDateString(dateString);
  const monthNames = {
    short: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
    long: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
  };
  return short ? monthNames.short[month - 1] : monthNames.long[month - 1];
}

/**
 * Get year from date string (YYYY-MM-DD)
 * WITHOUT any timezone conversion
 */
export function getYearFromDateString(dateString: string): number {
  return parseDateString(dateString).year;
}

/**
 * Get weekday name from date string (YYYY-MM-DD)
 * WITHOUT any timezone conversion
 * Uses Date object only for weekday calculation
 */
export function getWeekdayFromDateString(dateString: string, short: boolean = false): string {
  const { day, month, year } = parseDateString(dateString);
  const weekdayNames = {
    short: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    long: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']
  };

  // Create date at noon to avoid timezone issues
  const date = new Date(year, month - 1, day, 12, 0, 0);
  const dayOfWeek = date.getDay();

  return short ? weekdayNames.short[dayOfWeek] : weekdayNames.long[dayOfWeek];
}

/**
 * Compare two date strings (YYYY-MM-DD)
 * Returns: -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export function compareDateStrings(date1: string, date2: string): number {
  if (date1 < date2) return -1;
  if (date1 > date2) return 1;
  return 0;
}
